import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';

import { TemplateActions } from '../../../../../Services/_actions/index.js';
import { useSnackBar } from '../../../../FlowBuilder/Common/SnackBar/index.tsx';
import CommonTable, { EllipsisWithTooltip } from '../../../../Shared/Tables/TableV1.tsx';
import Pagination from '../../../../FlowBuilder/Common/Pagination/index.tsx';
import DynamicBreadcrumbs from '../../../../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';

// ─── types ────────────────────────────────────────────────────────────────────
interface OptinRow {
  'Phone Number'?: string;
  'Company ID'?: string;
  'Opted In'?: boolean;
  'Created At'?: string;
  'Updated At'?: string;
  'Source'?: string;
}

// ─── helpers ──────────────────────────────────────────────────────────────────
const OptedInChip: React.FC<{ value?: boolean }> = ({ value }) => (
  <Chip
    label={value ? 'Opted In' : 'Opted Out'}
    color={value ? 'success' : 'error'}
    size="small"
  />
);

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

// ─── columns ──────────────
const buildColumns = (page: number, rowsPerPage: number) => [
  {
    header: '#',
    accessorKey: '_index',
    size: 55,
    enableSorting: false,
    Cell: ({ row }: any) => (
      <Typography sx={{ fontSize: 12.25, color: 'text.secondary' }}>
        {page * rowsPerPage + row.index + 1}
      </Typography>
    ),
  },
  {
    header: 'Phone Number',
    accessorKey: 'Phone Number',
    size: 170,
    Cell: ({ row }: any) => (
      <EllipsisWithTooltip
        text={row.original?.['Phone Number'] || '—'}
        fontWeight={500}
      />
    ),
  },
  {
    header: 'Company ID',
    accessorKey: 'Company ID',
    size: 150,
    Cell: ({ row }: any) => (
      <EllipsisWithTooltip text={row.original?.['Company ID'] || '—'} />
    ),
  },
  {
    header: 'Source',
    accessorKey: 'Source',
    size: 130,
    Cell: ({ row }: any) => (
      <EllipsisWithTooltip text={row.original?.['Source'] || '—'} />
    ),
  },
  {
    header: 'Status',
    accessorKey: 'Opted In',
    size: 120,
    Cell: ({ row }: any) => (
      <OptedInChip value={row.original?.['Opted In']} />
    ),
  },
  {
    header: 'Created At',
    accessorKey: 'Created At',
    size: 180,
    Cell: ({ row }: any) => (
      <EllipsisWithTooltip
        text={formatDate(row.original?.['Created At'])}
        maxWidth={200}
      />
    ),
  },
  {
    header: 'Updated At',
    accessorKey: 'Updated At',
    size: 180,
    Cell: ({ row }: any) => (
      <EllipsisWithTooltip
        text={formatDate(row.original?.['Updated At'])}
        maxWidth={200}
      />
    ),
  },
];

// ─── component ────────────────────────────────────────────────────────────────
const OptinUsers: React.FC = () => {
  const actions = TemplateActions();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();

  // state
  const [rows, setRows] = useState<OptinRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // ── breadcrumbs ────────────────────────────────────────────────────────────
  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Optin Users' },
  ];

  const handleBreadcrumbClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: any
  ) => {
    if (item?.href) {
      navigate(item.href);
    }
  };

  // ── fetch ─────────────────────────────────────────────────────────────────
  const fetchOptinList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await actions.getOptinList();

      if (res) {
        const formattedRows = Array.isArray(res) ? res : [res];
        setRows(formattedRows);
        setTotalCount(formattedRows.length);
      }
    } catch {
      showSnackBar('Failed to fetch optin users', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptinList();
  }, [fetchOptinList]);

  // ── download ──────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    setDownloading(true);
    try {
      await actions.downloadOptinList();
    } catch {
      showSnackBar('Failed to download optin list', 'error');
    } finally {
      setDownloading(false);
    }
  };

  // ── pagination ────────────────────────────────────────────────────────────
  const handleChangePage = (_: any, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (e: any) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const renderBottomToolbar = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e0e0e0' }}>
      <Pagination
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Box>
  );

  const columns = buildColumns(page, rowsPerPage);

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', p: 2 }}>

      {/* HEADER */}
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        
        {/* LEFT */}
        <Grid item>
          <DynamicBreadcrumbs
            items={breadcrumbsData}
            onBreadcrumbClick={handleBreadcrumbClick}
          />

          <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
            Optin Users
          </Typography>
        </Grid>

        {/* RIGHT */}
        <Grid item>
          <Box sx={{ display: 'flex', gap: 1 }}>
            
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchOptinList}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Button
            variant="contained"
            color="primary" 
              size="small"
              startIcon={
                downloading
                  ? <CircularProgress size={14} color="inherit" />
                  : <DownloadIcon />
              }
              onClick={handleDownload}
              disabled={downloading}
              sx={{
                fontSize: 12,
              }}
            >
              {downloading ? 'Downloading...' : 'Download'}
            </Button>

          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 1 }} />

      {/* TABLE */}
      <CommonTable
        columns={columns}
        data={rows}
        isLoading={loading}
        enableTopToolbar={false}
        enableBottomToolbar
        enableStickyHeader
        renderBottomToolbar={renderBottomToolbar}
        state={{ isLoading: loading, sorting: [] }}
        initialState={{ density: 'compact' }}
        muiTableContainerProps={{
          sx: { maxHeight: 'calc(100vh - 260px)' }
        }}
      />
    </Box>
  );
};

export default OptinUsers;